"use client";

import { getData, postData } from "@/request/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export type AuthUser = {
  id: string;
  username: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  emailVerified: boolean;
};

export type CheckPhoneResult = {
  exists: boolean;
  status: "existing_user" | "new_user" | "pending_signup";
  email?: string;
  emailMasked?: string;
  loginMethods?: Array<"password" | "email_code">;
  // Where a login code goes: SMS to a Saudi number, email otherwise.
  codeChannel?: "sms" | "email";
};

type AuthResponse<T> = {
  success: boolean;
  message: string;
  data: T;
};

const authKeys = {
  currentUser: ["auth", "current-user"] as const,
};

const unwrap = async <T>(request: Promise<{ data: AuthResponse<T> }>) => {
  const response = await request;
  return response.data;
};

export const getAuthErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string } | undefined)?.message ||
      error.message ||
      "Request failed"
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong";
};

export function useAuthUser() {
  return useQuery({
    queryKey: authKeys.currentUser,
    retry: false,
    queryFn: async (): Promise<AuthUser | null> => {
      try {
        const response = await unwrap<{ user: AuthUser }>(getData("auth/me"));
        return response.data.user;
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          [401, 403].includes(error.response?.status || 0)
        ) {
          return null;
        }

        throw error;
      }
    },
  });
}

export function useCheckPhoneMutation() {
  return useMutation({
    mutationFn: async (payload: { phone: string }) => {
      const response = await unwrap<CheckPhoneResult>(
        postData("auth/check-phone", payload),
      );
      return response.data;
    },
  });
}

export function useSignupMutation() {
  return useMutation({
    mutationFn: async (payload: {
      username: string;
      email: string;
      phone: string;
      password: string;
      confirmPassword: string;
    }) => {
      return unwrap<{
        email: string;
        phone: string;
        /** the code also went out by SMS */
        sms?: boolean;
        nextStep: string;
      }>(postData("auth/signup", payload));
    },
  });
}

export function useVerifySignupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { email?: string; phone?: string; code: string }) => {
      const response = await unwrap<{ user: AuthUser }>(
        postData("auth/signup/verify", payload),
      );
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
    },
  });
}

export function useResendSignupCodeMutation() {
  return useMutation({
    mutationFn: async (payload: { email?: string; phone?: string }) => {
      const response = await unwrap<{ email: string; phone?: string; sms?: boolean }>(
        postData("auth/signup/resend", payload),
      );
      return response;
    },
  });
}

export function useSendLoginCodeMutation() {
  return useMutation({
    mutationFn: async (payload: { phone: string }) => {
      const response = await unwrap<{ emailMasked: string; channel?: "sms" | "email" }>(
        postData("auth/login/code/send", payload),
      );
      return response;
    },
  });
}

export function useLoginWithPasswordMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { phone: string; password: string }) => {
      const response = await unwrap<{ user: AuthUser }>(
        postData("auth/login/password", payload),
      );
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
    },
  });
}

export function useLoginWithCodeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { phone: string; code: string }) => {
      const response = await unwrap<{ user: AuthUser }>(
        postData("auth/login/code/verify", payload),
      );
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
    },
  });
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: async (payload: { phone: string }) => {
      const response = await unwrap<{
        emailMasked: string;
        retryAfter: number;
      }>(postData("auth/password/forgot", payload));
      return response;
    },
  });
}

export function useResetPasswordMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      phone: string;
      code: string;
      password: string;
      confirmPassword: string;
    }) => {
      const response = await unwrap<{ user: AuthUser }>(
        postData("auth/password/reset", payload),
      );
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
    },
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await unwrap<Record<string, never>>(
        postData("auth/logout", {}),
      );
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
      await queryClient.invalidateQueries({ queryKey: ["Profile"] });
    },
  });
}
